import type { ReactNode } from 'react';
import { useAppUser } from '../../../../lib/auth/use-app-user.js';

export interface AdminPermissionGateProps {
  /** Passes if the user holds ANY of these roles. Omit to gate on permissions only. */
  anyRole?: string[];
  /** Passes if the user holds ANY of these permissions. Omit to gate on roles only. */
  anyPermission?: string[];
  children: ReactNode;
  /** Rendered while `data.roles`/`permissions` are not yet known -- defaults to nothing, since this is a visibility convenience, not a loading boundary. */
  fallback?: ReactNode;
}

/**
 * UX-only visibility gate for admin UI (nav entries, action buttons) --
 * exactly like RequireRole/RequireAdmin in apps/web/src/lib/auth/route-guards.tsx,
 * this grants no real access and must never be treated as a security
 * boundary. Every backend admin route independently re-resolves roles/
 * permissions from Neon on every request regardless of what this
 * component decides to render. See docs/security-model.md "Frontend
 * guards are UX only".
 */
export function AdminPermissionGate({
  anyRole,
  anyPermission,
  children,
  fallback = null,
}: AdminPermissionGateProps) {
  const { data, isLoading } = useAppUser();

  if (isLoading || !data) return <>{fallback}</>;

  const roleOk = !anyRole || anyRole.some((role) => data.roles.includes(role));
  const permissionOk = !anyPermission || anyPermission.some((p) => data.permissions.includes(p));

  if (!roleOk || !permissionOk) return <>{fallback}</>;

  return <>{children}</>;
}
